import { useState, useEffect, useRef } from "react";
import { Transition } from "@headlessui/react";
import {
  ChevronDownIcon,
  UsersIcon,
  ClockIcon,
} from "@heroicons/react/20/solid";

function DropdownButton({
  label,
  options,
  onOptionSelect,
  disabled = false,
  fullWidth = false,
  scheduleData = {}, // New prop for schedule data
  isLoadingScheduleData = false, // New prop for loading state
  onOpen = null, // New prop to trigger when dropdown opens
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState("left-0");
  const dropdownRef = useRef(null);

  const toggleDropdown = () => {
    if (disabled) return;

    if (!isDropdownOpen) {
      // Calculate position when opening
      const buttonRect = dropdownRef.current?.getBoundingClientRect();
      const menuWidth = 224; // w-56 = 14rem = 224px
      const windowWidth = window.innerWidth;

      if (buttonRect) {
        const spaceOnRight = windowWidth - buttonRect.right;
        const spaceOnLeft = buttonRect.left;

        // If there's not enough space on the right, position on the left
        if (spaceOnRight < menuWidth && spaceOnLeft >= menuWidth) {
          setMenuPosition("right-0");
        } else {
          setMenuPosition("left-0");
        }
      }

      // Trigger data loading when opening
      if (onOpen) {
        onOpen();
      }
    }

    setIsDropdownOpen((prev) => !prev);
  };

  const handleOptionClick = (option) => {
    onOptionSelect(option);
    setIsDropdownOpen(false);
  };

  fullWidth = fullWidth ? "w-full" : "inline-block";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={`relative text-left ${fullWidth}`} ref={dropdownRef}>
      <button
        className={`inline-flex justify-between gap-x-1.5 w-full rounded-md px-3 -ml-1 py-2 items-center text-sm leading-none font-normal text-gray-900 dark:text-gray-200 border-0 transition-colors duration-150 ${
          disabled
            ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
            : isDropdownOpen
            ? "bg-gray-50 dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-white/[0.06]"
            : "bg-gray-50 dark:bg-gray-800 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/[0.06]"
        }`}
        onClick={toggleDropdown}
        disabled={disabled}
      >
        <div className="inline-flex w-full items-center gap-x-1.5">
          <span className="text-gray-600 whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
            {label}
          </span>
        </div>
        <ChevronDownIcon
          className={`-mr-1 h-5 w-5 ${
            disabled ? "text-gray-300" : "text-gray-500"
          }`}
          aria-hidden="true"
        />
      </button>

      <Transition
        show={isDropdownOpen}
        enter="transition ease-out duration-100 transform"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75 transform"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
        className={`absolute ${menuPosition} z-50 mt-2 w-56 origin-top-right divide-y divide-gray-100 dark:divide-gray-700 rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 focus:outline-none`}
      >
        <div className="p-4 max-h-60 overflow-y-auto">
          {options.length === 0 ? (
            <p className="text-gray-600 dark:text-gray-300 text-sm">No options available</p>
          ) : (
            <>
              <div className="pb-2">
                <p className="text-xsm uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {label}
                </p>
              </div>
              {options.map((option) => {
                const weekData = scheduleData[option.id];
                const isEmpty =
                  !weekData ||
                  weekData.numGuards === 0 ||
                  weekData.totalHours === 0;

                return (
                  <div
                    key={option.id}
                    className={`pl-2 py-3 -ml-2 -mr-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer flex items-center justify-between hover:bg-gray-50 dark:hover:bg-white/[0.06] rounded-md ${
                      isEmpty
                        ? "opacity-50 cursor-not-allowed hover:bg-white"
                        : ""
                    }`}
                    onClick={() => {
                      if (!isEmpty) {
                        handleOptionClick(option);
                      }
                    }}
                  >
                    <div className="flex flex-col gap-1">
                      <div className="font-medium">{option.name}</div>
                      {isLoadingScheduleData ? (
                        <div className="text-xs text-gray-400">Loading...</div>
                      ) : weekData ? (
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <div className="flex items-center gap-1">
                            <span>
                              {weekData.numGuards}{" "}
                              {weekData.numGuards === 1 ? "guard" : "guards"}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span>
                              {weekData.totalHours}{" "}
                              {weekData.totalHours === 1 ? "hour" : "hours"}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400">No data</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </Transition>
    </div>
  );
}

export { DropdownButton };
